import { useDebounce } from '@/hooks/useDebounce.ts'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import useAudioStore from '../../shared/store/audio.store.tsx'
import { MIX_ID } from '../../shared/store/tracks.store.tsx'
import useUiStore from '../../shared/store/ui.store.tsx'
import Icon from '../common/Icon'
import MediaFilesList from './MediaFiles/MediaFilesList.tsx'
import ScriptsBoxList from './Scripts/ScriptsBoxList.tsx'
import SoundDesignBoxList from './SoundDesign/SoundDesignBoxList.tsx'
import EffectsBoxList from './SoundEffect/SoundEffectsList.tsx'
import VoiceBoxList from './Voices/VoiceBoxList.tsx'
import VerticalScrollArea from '@/components/common/VerticalScrollArea.tsx'

export enum SidebarType {
  Scripts = 'scripts',
  SoundDesigns = 'sound-designs',
  MediaFiles = 'media-files',
  Voices = 'Voices',
  SoundEffects = 'sound-effects',
}

export const SidebarTypes = {
  [SidebarType.Scripts]: ScriptsBoxList,
  [SidebarType.Voices]: VoiceBoxList,
  [SidebarType.MediaFiles]: MediaFilesList,
  [SidebarType.SoundDesigns]: SoundDesignBoxList,
  [SidebarType.SoundEffects]: EffectsBoxList,
} as const

export interface SpecificSidebarProps {
  goBack?: (() => void) | undefined
  children?: React.ReactNode
}

export interface SidebarProps extends SpecificSidebarProps {
  title: string
  searchKey?: string
  searchAction?: (key: string) => void
  initialSearchTerm?: string
  fetchMoreData?: () => void
  noResults?: boolean
  className?: string
}

const Sidebar = ({
  goBack,
  title,
  searchKey,
  children,
  fetchMoreData,
  searchAction,
  initialSearchTerm,
  noResults,
  className,
}: SidebarProps) => {
  const { t } = useTranslation()
  const setIsSidebarOpen = useUiStore((state) => state.setIsSidebarOpen)
  const { stopPlayback, currentAudioId } = useAudioStore(
    useShallow((state) => ({
      stopPlayback: state.stopPlayback,
      currentAudioId: state.currentAudioId,
    }))
  )
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? '')
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const searchActionRef = useRef(searchAction)
  searchActionRef.current = searchAction

  const onClose = (): void => {
    if (currentAudioId !== MIX_ID) {
      stopPlayback()
    }
    setIsSidebarOpen(false)
  }

  useEffect(() => {
    searchActionRef.current?.(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  const fetchMoreRef = useRef(fetchMoreData)
  fetchMoreRef.current = fetchMoreData

  const handleScroll = useCallback(() => {
    if (sidebarRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = sidebarRef.current
      if (scrollTop + clientHeight >= scrollHeight * 0.85) {
        fetchMoreRef.current?.()
      }
    }
  }, [])

  useEffect(() => {
    const sidebarElement = sidebarRef.current
    if (!sidebarElement) return

    sidebarElement.addEventListener('scroll', handleScroll)
    return () => sidebarElement.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <div className="flex h-full w-full flex-col overflow-hidden border border-zinc-900 bg-zinc-900 shadow-xl">
      <div className="relative flex h-9 items-center justify-center border-b border-zinc-900 bg-zinc-700 px-2">
        {goBack && (
          <button
            type="button"
            className="absolute left-2 text-white"
            onClick={goBack}
          >
            <Icon name="arrowLeft" size={20} />
          </button>
        )}
        <h2 className="text-sm font-medium text-white">{t(title)}</h2>
        <button
          type="button"
          className="absolute right-2 text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          <Icon name="xMark" width={20} height={20} />
        </button>
      </div>
      {searchAction && searchKey && (
        <div className="relative flex items-center gap-2 bg-zinc-800">
          <div className="pointer-events-none flex items-center pl-4">
            <Icon name="search" fill="#9ca3af" width={20} height={20} />
          </div>
          <input
            type="text"
            className="block w-full bg-zinc-800 py-3 pl-0 pr-4 text-sm placeholder:text-sm focus:outline-none"
            placeholder={t(searchKey)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}
      <VerticalScrollArea
        className={`relative flex-1 overflow-y-hidden ${className || ''}`}
        ref={sidebarRef}
      >
        {children}
        {noResults && (
          <p className="text-center text-gray-500">
            {t('ls_sidebar_no_results')}
          </p>
        )}
      </VerticalScrollArea>
    </div>
  )
}

export default Sidebar
